import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useSettings } from '../hooks/useApi';

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

const DEFAULTS = [
  {
    key: 'web_virtual',
    title: 'Web & Virtual',
    desc: 'Web Application and Virtual Tour 360° design services. Seamlessly connect your business to the online world.',
    icon: 'fas fa-globe',
  },
  {
    key: 'on_ground',
    title: 'On-Ground Interactive',
    desc: 'Interactive system services for event venues including Projection Mapping, Games, AR/VR, and sensor-based activations.',
    icon: 'fas fa-desktop',
  },
  {
    key: 'custom_tech',
    title: 'Custom Tech & AI',
    desc: 'Custom-made Hardware, Sensor, and AI Software development with powerful backend management systems.',
    icon: 'fas fa-robot',
  },
];

function AboutSection() {
  const { data: settings } = useSettings();
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });

  const s = settings || {};
  const aboutTitle = s.about_title || 'WHAT WE DO?';
  const aboutText = s.about_text || '';
  const aboutTextTh = s.about_text_th || '';
  const aboutImage = s.about_image || '/uploads/images/about/Interactive-Wall-and-Floor-2000x1119.png';
  const aboutImageMaxWidth = s.about_image_maxwidth || '100%';
  const aboutBgImage = s.about_bg_image || '';
  const aboutBgOpacity = parseFloat(s.about_bg_opacity) || 0.15;

  const categories = DEFAULTS.map(d => ({
    ...d,
    title: s[`${d.key}_title`] || d.title,
    desc: s[`${d.key}_desc`] || d.desc,
  }));

  return (
    <section
      id="about"
      className="section about"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {aboutBgImage && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: `url(${aboutBgImage})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: aboutBgOpacity,
          pointerEvents: 'none', zIndex: 0,
        }} />
      )}
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          className="section__header"
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ textAlign: 'left', maxWidth: '100%' }}
        >
          <h2 className="about__main-title">{aboutTitle}</h2>
          {aboutText && <p className="about__main-desc">{aboutText}</p>}
          {aboutTextTh && <p className="about__main-desc" style={{ opacity: 0.7, fontSize: '0.95em' }}>{aboutTextTh}</p>}
        </motion.div>

        {/* Single hero image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          style={{ margin: '40px auto', borderRadius: 16, overflow: 'hidden', maxWidth: aboutImageMaxWidth }}
        >
          <img
            src={aboutImage}
            alt="What We Do"
            loading="lazy"
            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 16 }}
          />
        </motion.div>

        {/* 3 category cards with icons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 30,
          marginTop: 20,
        }}>
          {categories.map((cat, idx) => (
            <motion.div
              key={cat.key}
              custom={idx}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              variants={cardVariants}
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: '30px 24px',
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'default',
              }}
              whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,187,170,0.15)' }}
            >
              <div style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0bb4aa, #0d9488)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <i className={cat.icon} style={{ fontSize: 24, color: '#fff' }} />
              </div>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#fff',
                marginBottom: 10,
              }}>
                {cat.title}
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.6,
                margin: 0,
              }}>
                {cat.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default AboutSection;
